import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, MoveUp, MoveDown, BookOpen, X } from "lucide-react";

/**
 * Editor de Ementa em Módulos.
 * - Cada módulo: título, descrição opcional, lista de tópicos, carga horária opcional.
 * - value: SyllabusModule[]   |   onChange: (modules) => void
 *
 * SyllabusModule = { title: string, description?: string, topics?: string[], hours?: number }
 */
export function SyllabusEditor({ value, onChange }) {
  const modules = Array.isArray(value) ? value : [];
  const [topicDraft, setTopicDraft] = useState({});

  const update = (idx, patch) => {
    const next = modules.map((m, i) => (i === idx ? { ...m, ...patch } : m));
    onChange(next);
  };

  const add = () => {
    const next = [...modules, { title: "", description: "", topics: [], hours: null }];
    onChange(next);
  };

  const remove = (idx) => {
    const next = modules.filter((_, i) => i !== idx);
    onChange(next);
  };

  const move = (idx, dir) => {
    const next = [...modules];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= next.length) return;
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    onChange(next);
  };

  const addTopic = (idx) => {
    const t = (topicDraft[idx] || "").trim();
    if (!t) return;
    const current = modules[idx]?.topics || [];
    update(idx, { topics: [...current, t] });
    setTopicDraft((s) => ({ ...s, [idx]: "" }));
  };

  const removeTopic = (idx, tIdx) => {
    const current = modules[idx]?.topics || [];
    update(idx, { topics: current.filter((_, i) => i !== tIdx) });
  };

  return (
    <div className="space-y-3" data-testid="syllabus-editor">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-[hsl(var(--primary))]" />
          <Label className="text-sm font-medium">Ementa em módulos (vai no verso do certificado)</Label>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={add}
          data-testid="syllabus-add-module"
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> Módulo
        </Button>
      </div>

      {modules.length === 0 && (
        <p className="text-xs text-muted-foreground italic px-1">
          Nenhum módulo adicionado. Clique em <strong>+ Módulo</strong> para começar.
        </p>
      )}

      <div className="space-y-2">
        {modules.map((m, idx) => (
          <Card key={idx} className="p-3 border-dashed" data-testid={`syllabus-module-${idx}`}>
            <div className="flex items-start gap-2">
              <div className="flex-shrink-0 w-7 h-7 rounded-full bg-[hsl(var(--primary))] text-white flex items-center justify-center text-xs font-semibold mt-1">
                {idx + 1}
              </div>
              <div className="flex-1 space-y-2 min-w-0">
                <Input
                  placeholder={`Título do Módulo ${idx + 1}`}
                  value={m.title || ""}
                  onChange={(e) => update(idx, { title: e.target.value })}
                  className="font-medium"
                  data-testid={`syllabus-title-${idx}`}
                />
                <Textarea
                  rows={2}
                  placeholder="Descrição/objetivo do módulo (opcional)"
                  value={m.description || ""}
                  onChange={(e) => update(idx, { description: e.target.value })}
                  className="text-sm"
                />

                {/* Tópicos */}
                <div>
                  <p className="text-[11px] font-medium text-muted-foreground mb-1">Tópicos abordados (opcional)</p>
                  {(m.topics || []).length > 0 && (
                    <ul className="space-y-1 mb-2">
                      {(m.topics || []).map((t, tIdx) => (
                        <li
                          key={tIdx}
                          className="text-xs bg-[hsl(var(--secondary))] rounded-md px-2 py-1 flex items-center justify-between"
                        >
                          <span>• {t}</span>
                          <button
                            type="button"
                            onClick={() => removeTopic(idx, tIdx)}
                            className="text-rose-600 hover:text-rose-800"
                            aria-label="Remover tópico"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="flex gap-1">
                    <Input
                      placeholder="Adicionar tópico…"
                      value={topicDraft[idx] || ""}
                      onChange={(e) => setTopicDraft((s) => ({ ...s, [idx]: e.target.value }))}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addTopic(idx);
                        }
                      }}
                      className="text-xs h-8"
                      data-testid={`syllabus-topic-input-${idx}`}
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => addTopic(idx)}
                      className="h-8 px-2"
                      data-testid={`syllabus-topic-add-${idx}`}
                    >
                      <Plus className="w-3 h-3" />
                    </Button>
                  </div>
                </div>

                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <Label className="text-[11px] text-muted-foreground">Carga horária (h)</Label>
                    <Input
                      type="number"
                      min={0}
                      step="0.5"
                      value={m.hours ?? ""}
                      onChange={(e) =>
                        update(idx, {
                          hours: e.target.value === "" ? null : Number(e.target.value),
                        })
                      }
                      className="h-8"
                      placeholder="Opcional"
                    />
                  </div>
                </div>
              </div>

              {/* Ações */}
              <div className="flex flex-col gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => move(idx, -1)}
                  disabled={idx === 0}
                  aria-label="Mover acima"
                  data-testid={`syllabus-up-${idx}`}
                >
                  <MoveUp className="w-3.5 h-3.5" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => move(idx, +1)}
                  disabled={idx === modules.length - 1}
                  aria-label="Mover abaixo"
                  data-testid={`syllabus-down-${idx}`}
                >
                  <MoveDown className="w-3.5 h-3.5" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-rose-600 hover:bg-rose-50"
                  onClick={() => remove(idx)}
                  aria-label="Excluir módulo"
                  data-testid={`syllabus-remove-${idx}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {modules.length > 0 && (
        <p className="text-[11px] text-muted-foreground italic">
          A ementa será impressa no verso do certificado quando emitido.
        </p>
      )}
    </div>
  );
}
